// DJB-248 / DJB-249: the "Planning" notepad next to the Accounts › Maps map.
// Pure helpers (no React, no Leaflet) so the list rules and the localStorage
// TTL are unit-testable; usePlanning.js wraps them in page state and
// PlanningPanel.js renders the list.
//
// The planning is an ordered list of machine ids (strings). Everything else
// shown per entry (name, triggers, online dot, address) is looked up in the
// already-loaded map data at render time — nothing but the ids and the
// order is stored, so a stale snapshot can never contradict the map.

export const PLANNING_STORAGE_KEY = 'fe-jukebox.maps.planning';

// DJB-249: the planning survives a reload for 10 hours after the operator
// last changed it (a day planning), then the list starts out empty again.
export const PLANNING_TTL_MS = 10 * 60 * 60 * 1000;

export const planningId = (value) => (value == null ? null : String(value));

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    // Safari throws on access in some private-mode / blocked-storage setups
    return null;
  }
};

const normalizeIds = (ids) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(ids) ? ids : []).forEach((value) => {
    const id = planningId(value);
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  });
  return out;
};

export const isPlanningExpired = (savedAt, now = Date.now()) =>
  !Number.isFinite(Number(savedAt)) || now - Number(savedAt) > PLANNING_TTL_MS;

// → { ids, savedAt } — an empty list (savedAt = now) when nothing is stored,
// the entry is malformed, or it is older than the TTL (that entry is removed).
export const readStoredPlanning = (now = Date.now()) => {
  const empty = { ids: [], savedAt: now };
  const store = storage();
  if (!store) return empty;
  let raw;
  try {
    raw = store.getItem(PLANNING_STORAGE_KEY);
  } catch (error) {
    return empty;
  }
  if (!raw) return empty;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    parsed = null;
  }
  if (!parsed || typeof parsed !== 'object' || isPlanningExpired(parsed.savedAt, now)) {
    try {
      store.removeItem(PLANNING_STORAGE_KEY);
    } catch (error) {
      // ignore
    }
    return empty;
  }
  const ids = normalizeIds(parsed.ids);
  return ids.length ? { ids, savedAt: Number(parsed.savedAt) } : empty;
};

// An empty planning removes the entry altogether (no stale timestamp).
export const writeStoredPlanning = (ids, savedAt = Date.now()) => {
  const store = storage();
  if (!store) return;
  try {
    const list = normalizeIds(ids);
    if (!list.length) {
      store.removeItem(PLANNING_STORAGE_KEY);
    } else {
      store.setItem(PLANNING_STORAGE_KEY, JSON.stringify({ savedAt, ids: list }));
    }
  } catch (error) {
    // quota / private mode: the planning just does not survive a reload
  }
};

// --- list operations (all return the same array when nothing changes) ---

export const addPlanned = (ids, machineId) => {
  const id = planningId(machineId);
  if (!id || ids.includes(id)) return ids;
  return [...ids, id];
};

export const removePlanned = (ids, machineId) => {
  const id = planningId(machineId);
  if (!id || !ids.includes(id)) return ids;
  return ids.filter((item) => item !== id);
};

// Move the entry at `from` so it ends up at index `to` (both in the current
// list); out-of-range or no-op moves return the input list.
export const movePlanned = (ids, from, to) => {
  if (
    !Number.isInteger(from) ||
    !Number.isInteger(to) ||
    from < 0 ||
    to < 0 ||
    from >= ids.length ||
    to >= ids.length ||
    from === to
  ) {
    return ids;
  }
  const next = ids.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// Drop `machineId` before / after `targetId` (drag & drop between entries).
export const movePlannedTo = (ids, machineId, targetId, { after = false } = {}) => {
  const id = planningId(machineId);
  const target = planningId(targetId);
  const from = ids.indexOf(id);
  const targetIndex = ids.indexOf(target);
  if (from < 0 || targetIndex < 0 || id === target) return ids;
  const without = ids.filter((item) => item !== id);
  let insertAt = without.indexOf(target);
  if (after) insertAt += 1;
  const next = without.slice();
  next.splice(insertAt, 0, id);
  return next.every((item, index) => item === ids[index]) ? ids : next;
};

// --- rendering helpers ---

// The ordered planning as map-data rows; ids the map data does not (yet)
// know are skipped, so the panel only ever shows real, pinned machines.
export const resolvePlanning = (ids, machines) => {
  const byId = new Map();
  (Array.isArray(machines) ? machines : []).forEach((machine) => {
    const id = planningId(machine && machine.machineId);
    if (id) byId.set(id, machine);
  });
  return ids.map((id) => byId.get(id)).filter(Boolean);
};

const clean = (value) => (value == null ? '' : String(value).trim());

// "Markt 1, 8000 Brugge" — street / nr / postcode come from the MachineContext
// machine with the same id (the map-data row only carries city + province,
// same situation as MappingIssues, DJB-247); falls back to what is there.
export const formatPlanningAddress = (row, full) => {
  const street = clean(full && full.street);
  const number = clean(full && full.housenumber);
  const postal = clean(full && full.postalcode);
  const city = clean(row && row.city) || clean(full && full.city);
  const province = clean(row && row.province) || clean(full && full.province);
  const streetPart = [street, number].filter(Boolean).join(' ');
  const cityPart = [postal, city].filter(Boolean).join(' ');
  const address = [streetPart, cityPart].filter(Boolean).join(', ');
  if (address) return address;
  return province || '—';
};
